import React, { useState, useEffect, useRef, useCallback } from "react";
import DistributorFormModal from "./DistributorFormModal";
import DistributorTable from "./DistributorTable";
import "../../assets/styles/common.css";
import "./Settings.css"; // Keep this for specific settings tab styles

// --- NEW: Accept getAuthHeaders as a prop ---
const DistributorSettings = ({ getAuthHeaders }) => {
  // --- END NEW ---
  const [distributors, setDistributors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [tempDistributor, setTempDistributor] = useState({
    companyName: "",
    contactPerson: "",
    contactNo: "",
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const actionMenuRefs = useRef({});

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // const API_URL = process.env.REACT_APP_BACKEND_API_URL;
  const { API_URL } = require("../../config");

  const API_BASE_URL = `${API_URL}/distributors`;

  // Function to fetch distributors from the backend
  // 💥 FIX: Added getAuthHeaders and API_BASE_URL to the dependency array.
  const fetchDistributors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_BASE_URL, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error("Authentication failed. Redirecting to login.");
          localStorage.removeItem("authToken");
          localStorage.removeItem("loggedInUser");
          localStorage.removeItem("isLoggedIn");
          window.location.href = "/login";
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDistributors(data);
    } catch (err) {
      console.error("Error fetching distributors:", err);
      setError("Failed to load distributors. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, API_BASE_URL]);

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  // Handle click outside of action menu (reused from UserSettings)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openMenuId &&
        actionMenuRefs.current[openMenuId] &&
        !actionMenuRefs.current[openMenuId].contains(event.target)
      ) {
        const ellipsisButton = event.target.closest(".ellipsis-button");
        if (
          ellipsisButton &&
          ellipsisButton.dataset.distributorId === String(openMenuId)
        ) {
          return;
        }
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const handleAddDistributor = () => {
    setEditingDistributor(null);
    setTempDistributor({
      companyName: "",
      contactPerson: "",
      contactNo: "",
    });
    setShowModal(true);
    setOpenMenuId(null);
  };

  const handleEditDistributor = (distributor) => {
    setEditingDistributor(distributor);
    setTempDistributor({ ...distributor });
    setShowModal(true);
    setOpenMenuId(null);
  };

  // 💥 FIX: Added API_BASE_URL, getAuthHeaders, and fetchDistributors to the dependency array.
  const handleDeleteDistributor = useCallback(
    async (id) => {
      if (window.confirm("Are you sure you want to delete this distributor?")) {
        try {
          const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: "DELETE",
            headers: getAuthHeaders(),
          });

          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              console.error("Authentication failed. Redirecting to login.");
              localStorage.removeItem("authToken");
              localStorage.removeItem("loggedInUser");
              localStorage.removeItem("isLoggedIn");
              window.location.href = "/login";
              return;
            }
            const errorData = await response.json();
            throw new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
          }
          await fetchDistributors();
          console.log(`Distributor with ID ${id} deleted successfully.`);
        } catch (err) {
          console.error("Error deleting distributor:", err);
          setError("Failed to delete distributor: " + err.message);
        } finally {
          setOpenMenuId(null);
        }
      }
    },
    [API_BASE_URL, getAuthHeaders, fetchDistributors]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTempDistributor((prev) => ({ ...prev, [name]: value }));
  };

  // 💥 FIX: Added API_BASE_URL, API_URL, getAuthHeaders, editingDistributor, and fetchDistributors to the dependency array.
  const handleSaveDistributor = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !tempDistributor.companyName ||
        !tempDistributor.contactPerson ||
        !tempDistributor.contactNo
      ) {
        alert("Please fill in all fields.");
        return;
      }

      try {
        let response;
        let url = API_BASE_URL;
        let method = "POST";
        let bodyData = { ...tempDistributor };

        if (editingDistributor) {
          method = "PUT";
          url = `${API_BASE_URL}/${editingDistributor.id}`;
          delete bodyData.id;
        }

        response = await fetch(url, {
          method: method,
          headers: getAuthHeaders(),
          body: JSON.stringify(bodyData),
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.error("Authentication failed. Redirecting to login.");
            localStorage.removeItem("authToken");
            localStorage.removeItem("loggedInUser");
            localStorage.removeItem("isLoggedIn");
            window.location.href = "/login";
            return;
          }
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        await fetchDistributors();
        console.log(
          editingDistributor ? "Distributor updated." : "New distributor added."
        );
        setShowModal(false);
      } catch (err) {
        console.error("Error saving distributor:", err);
        setError(`Failed to save distributor: ${err.message}`);
      } finally {
        setOpenMenuId(null);
      }
    },
    [
      tempDistributor,
      editingDistributor,
      getAuthHeaders,
      fetchDistributors,
      API_BASE_URL,
    ]
  );

  const toggleRowActions = (id, event) => {
    event.stopPropagation();
    if (event.currentTarget) {
      event.currentTarget.dataset.distributorId = id;
    }
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setOpenMenuId(null);
  };

  return (
    <div className="settings-content-section">
      <div className="settings-header-actions">
        <h2>Distributor List</h2>
        <div className="global-action-buttons">
          <button onClick={handleAddDistributor} className="btn-primary">
            <span className="button-icon">&#x2795;</span> Add New Distributor
          </button>
        </div>
      </div>

      <DistributorTable
        distributors={distributors}
        isLoading={isLoading}
        error={error}
        openMenuId={openMenuId}
        actionMenuRefs={actionMenuRefs}
        toggleRowActions={toggleRowActions}
        handleEditDistributor={handleEditDistributor}
        handleDeleteDistributor={handleDeleteDistributor}
        setOpenMenuId={setOpenMenuId}
      />

      <DistributorFormModal
        showModal={showModal}
        editingDistributor={editingDistributor}
        tempDistributor={tempDistributor}
        handleChange={handleChange}
        handleSaveDistributor={handleSaveDistributor}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default DistributorSettings;
